import React, { useState, useEffect } from 'react';
import { 
  FaCrown, 
  FaUser, 
  FaCheckCircle, 
  FaAd, 
  FaUsers, 
  FaGem, 
  FaSpinner,
  FaChartLine,
  FaCalendarAlt,
  FaBullseye,
  FaMoneyBillWave,
  FaFileAlt,
  FaUserShield,
  FaChartPie
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, functions } from '../firebase';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../contexts/AuthContext';

const Membership = () => {
  const [upgradingLoading, setUpgradingLoading] = useState(false);
  const [membershipLevel, setMembershipLevel] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser) return;

    // Use onSnapshot instead of getDoc for real-time updates
    const unsubscribe = onSnapshot(
      doc(db, 'users', currentUser.uid),
      (docSnapshot) => {
        if (docSnapshot.exists()) {
          const userData = docSnapshot.data();
          setMembershipLevel(userData.membershipLevel || 'free');
          setIsAdmin(userData.isAdmin || false);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Error fetching user membership:', error);
        toast.error('Failed to fetch membership status');
        setIsLoading(false);
      }
    );

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [currentUser]);

  // Function for upgrading to premium via Stripe
  const handleUpgradeToPremium = async () => {
    setUpgradingLoading(true);
    try {
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession();
      
      if (result.data && result.data.url) {
        // Redirect to Stripe Checkout
        window.location.href = result.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (error) {
      console.error('Error creating checkout session:', error);
      setUpgradingLoading(false);
      
      if (error.code === 'functions/failed-precondition') {
        toast.error(error.message);
      } else if (error.code === 'functions/unauthenticated') {
        toast.error('Please log in to subscribe');
      } else {
        toast.error('Failed to start checkout. Please try again.');
      }
    }
  };

  const renderMembershipCard = (type) => {
    const tierConfigs = {
      free: {
        title: 'Free Tier',
        price: '£0.00',
        borderColor: 'primary',
        bgColor: 'primary-50',
        darkBgColor: 'primary-900',
        icon: <FaUser className="h-5 w-5" />,
        description: 'Basic access to Cashsule features'
      },
      premium: {
        title: 'Advanced Premium',
        price: '£1.29/month',
        borderColor: 'amber',
        bgColor: 'amber-50',
        darkBgColor: 'amber-900/20',
        icon: <FaCrown className="h-5 w-5 premium-icon" />,
        description: 'Price locked at £1.29/month. Price changes will be notified 60 days in advance.'
      },
      promo: {
        title: 'Promo Premium',
        price: 'Invitation Only',
        borderColor: 'purple',
        bgColor: 'purple-50',
        darkBgColor: 'purple-900/20',
        icon: <FaGem className="h-5 w-5" />,
        description: 'Special premium access provided at no charge'
      }
    };

    const config = tierConfigs[type];

    return (
      <div className={`border rounded-lg p-4 ${
        membershipLevel === type && type !== 'premium'
          ? `border-${config.borderColor}-500 dark:border-${config.borderColor}-400 bg-${config.bgColor} dark:bg-${config.darkBgColor} ring-2 ring-${config.borderColor}-500 dark:ring-${config.borderColor}-400`
          : membershipLevel === type && type === 'premium'
          ? 'border-amber-500 dark:border-amber-400 ring-2 ring-amber-500 dark:ring-amber-400 premium-card'
          : type === 'premium' 
          ? 'border-amber-300 dark:border-amber-700 premium-card-inactive'
          : 'border-gray-200 dark:border-gray-700'
      }`}>
        <style jsx>{`
          @keyframes gradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          
          .premium-title, .premium-icon {
            background: linear-gradient(90deg, #f59e0b, #fbbf24, #f59e0b, #d97706);
            background-size: 300% 300%;
            animation: gradient 3s ease infinite;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            text-fill-color: transparent;
          }
          
          .premium-card {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.3), rgba(217, 119, 6, 0.15), rgba(245, 158, 11, 0.2));
            background-size: 200% 200%;
            animation: gradient 10s ease infinite;
            border-width: 2px;
            box-shadow: 0 5px 15px rgba(251, 191, 36, 0.2);
          }
          
          .premium-card-inactive {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(217, 119, 6, 0.08), rgba(245, 158, 11, 0.1));
            background-size: 200% 200%;
            animation: gradient 10s ease infinite;
          }
          
          .premium-button {
            background: linear-gradient(45deg, #f59e0b, #d97706, #b45309);
            background-size: 200% 200%;
            animation: gradient 3s ease infinite;
            box-shadow: 0 4px 15px rgba(245, 158, 11, 0.4);
            transition: all 0.3s ease;
            transform: translateY(0);
          }
          
          .premium-button:hover:not(:disabled) {
            box-shadow: 0 6px 20px rgba(245, 158, 11, 0.5);
            transform: translateY(-2px);
          }
          
          .premium-button:active:not(:disabled) {
            transform: translateY(0);
            box-shadow: 0 2px 10px rgba(245, 158, 11, 0.3);
          }
          
          .dark .premium-card {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.15), rgba(217, 119, 6, 0.1), rgba(245, 158, 11, 0.2));
            background-size: 200% 200%;
            animation: gradient 10s ease infinite;
            box-shadow: none;
          }
          
          .dark .premium-card-inactive {
            background: linear-gradient(135deg, rgba(251, 191, 36, 0.07), rgba(217, 119, 6, 0.05), rgba(245, 158, 11, 0.1));
            background-size: 200% 200%;
            animation: gradient 10s ease infinite;
          }
        `}</style>
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            {type === 'premium' ? (
              <div className="premium-icon">
                {config.icon}
              </div>
            ) : (
              config.icon
            )}
            <h4 className={`font-semibold text-gray-900 dark:text-white ${type === 'premium' ? 'premium-title' : ''}`}>
              {config.title}
            </h4>
          </div>
          {membershipLevel === type && (
            <span className={`text-xs font-medium px-2.5 py-0.5 rounded ${
              type === 'premium' 
                ? 'bg-amber-100 dark:bg-amber-800/50 text-amber-800 dark:text-amber-200' 
                : 'bg-primary-100 dark:bg-primary-800 text-primary-800 dark:text-primary-200'
            }`}>Current</span>
          )}
        </div>
        <p className={`text-xl font-bold mb-2 ${type === 'premium' ? 'premium-title' : 'text-gray-900 dark:text-white'}`}>{config.price}</p>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{config.description}</p>
        <ul className="space-y-2 mb-4">
          <li className="flex items-center">
            <FaChartLine className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
            <span className="text-sm text-gray-900 dark:text-white">Advanced expense tracking with detailed categorisation</span>
          </li>
          <li className="flex items-center">
            <FaCalendarAlt className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
            <span className="text-sm text-gray-900 dark:text-white">Full recurring payment management</span>
          </li>
          <li className="flex items-center">
            <FaBullseye className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
            <span className="text-sm text-gray-900 dark:text-white">Smart budget planning with real-time tracking</span>
          </li>
          <li className="flex items-center">
            <FaMoneyBillWave className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
            <span className="text-sm text-gray-900 dark:text-white">Comprehensive income tracking</span>
          </li>
          <li className="flex items-center">
            <FaFileAlt className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
            <span className="text-sm text-gray-900 dark:text-white">Professional PDF statement generation</span>
          </li>
          {type === 'free' ? (
            <>
              <li className="flex items-center opacity-50">
                <FaAd className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                <span className="text-sm text-gray-900 dark:text-white">Includes advertisements</span>
              </li>
              <li className="flex items-center opacity-50">
                <FaChartPie className="h-4 w-4 text-gray-400 dark:text-gray-500 mr-2" />
                <span className="text-sm text-gray-900 dark:text-white">No access to all Premium features</span>
              </li>
            </>
          ) : (
            <>
              <li className="flex items-center">
                <FaAd className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
                <span className="text-sm text-gray-900 dark:text-white">Ad-free experience</span>
              </li>
              <li className="flex items-center">
                <FaChartPie className="h-4 w-4 text-green-500 dark:text-green-400 mr-2" />
                <span className="text-sm text-gray-900 dark:text-white">Full access to all Premium features</span>
              </li>
            </>
          )}
        </ul>
        {type === 'premium' && membershipLevel === 'free' && (
          <button 
            onClick={handleUpgradeToPremium}
            disabled={upgradingLoading}
            className="premium-button w-full py-3 px-6 rounded-full text-white font-bold transition-all focus:outline-none disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {upgradingLoading ? (
              <span className="flex items-center justify-center">
                <FaSpinner className="animate-spin mr-2 h-5 w-5" />
                Processing...
              </span>
            ) : (
              <>
                <FaCrown className="mr-2 h-5 w-5" />
                Upgrade Now
              </>
            )}
          </button>
        )}
      </div>
    );
  };

  // Order tiers with current membership first
  const getOrderedTiers = () => {
    const baseTiers = ['free', 'premium'];
    // Only include promo tier if user has promo membership
    const allTiers = membershipLevel === 'promo' ? [...baseTiers, 'promo'] : baseTiers;
    
    if (!membershipLevel || !allTiers.includes(membershipLevel)) return allTiers;

    return [
      membershipLevel,
      ...allTiers.filter(tier => tier !== membershipLevel)
    ];
  };

  if (isLoading) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden p-6">
        <div className="flex items-center justify-center h-32">
          <FaSpinner className="h-8 w-8 animate-spin text-primary-500" />
        </div>
      </div>
    );
  }

  if (isAdmin) {
    return (
      <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
        <div className="p-6">
          <div className="flex items-center justify-center space-x-2 bg-gradient-to-r from-purple-500 to-indigo-600 text-white rounded-lg p-4">
            <FaUserShield className="h-6 w-6" />
            <span className="text-lg font-semibold">Administrator Account</span>
          </div>
          <p className="text-center mt-4 text-sm text-gray-500 dark:text-gray-400">
            You have full administrative privileges
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 shadow-lg rounded-lg overflow-hidden">
      <div className="p-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Your Membership</h3>

        {/* Current Membership Display */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-6">
          <div className="flex items-center">
            <div className={`rounded-full p-3 mr-3 ${
              membershipLevel === 'premium' 
                ? 'bg-gradient-to-r from-amber-400 to-amber-600 text-white dark:from-amber-500 dark:to-amber-700 shadow-md' 
                : membershipLevel === 'promo'
                ? 'bg-purple-100 dark:bg-purple-900 text-purple-600 dark:text-purple-400'
                : 'bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-400'
            }`}>
              {membershipLevel === 'premium' 
                ? <FaCrown className="h-5 w-5" /> 
                : membershipLevel === 'promo'
                ? <FaGem className="h-5 w-5" />
                : <FaUser className="h-5 w-5" />
              }
            </div>
            <div className="flex-1">
              <p className={`font-medium ${
                membershipLevel === 'premium' 
                  ? 'premium-title' 
                  : 'text-gray-900 dark:text-white'
              }`}>
                {membershipLevel === 'premium' 
                  ? 'Advanced Premium Tier' 
                  : membershipLevel === 'promo' 
                    ? 'Promo Premium Tier' 
                    : 'Free Tier'}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {membershipLevel === 'premium' 
                  ? 'Your subscription renews monthly at £1.29' 
                  : membershipLevel === 'promo' 
                    ? 'Special premium access provided at no charge' 
                    : 'Basic access to Cashsule features'}
              </p>
            </div>
          </div>
        </div>

        {/* All Membership Tiers */}
        <div className="space-y-4">
          {getOrderedTiers().map(tier => (
            <div key={`membership-tier-${tier}`}>
              {renderMembershipCard(tier)}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Membership;
